










































import { Component, Vue, Watch } from 'vue-property-decorator'
import Map from 'ol/Map'
import { Feature, View } from 'ol'
import ZoomButtons from '@/components/map/controls/ZoomButtons.vue'
import ViewMode from '@/components/map/controls/ViewMode.vue'
import MapButton from '@/components/map/controls/MapButton.vue'
import {
  BaseMap,
  defaultProjection, defaultScaleClusterExplosion,
  defaultScales,
  getResolutionFromScale,
  getScaleFromResolution, MapSingleton
} from '@/utils/map'
import ScaleSelect from '@/components/map/controls/ScaleSelect.vue'
import debounce from 'lodash/debounce'
import clone from 'lodash/clone'
import chunk from 'lodash/chunk'
import Draw, { DrawEvent } from 'ol/interaction/Draw'
import GeometryType from 'ol/geom/GeometryType'
import VectorSource from 'ol/source/Vector'
import LineString from 'ol/geom/LineString'
import Polygon from 'ol/geom/Polygon'
import SearchInput from '@/components/map/controls/SearchInput.vue'
import ContextWrapper from '@/orrm/wrapper/context'
import { context, map, search } from '@/store/modules'
import LayerWrapper from '@/orrm/wrapper/layer'
import { Actualite } from '@/orrm/model/actualite'
import { FilterSpec } from 'coloquent/dist/FilterSpec'
import Fetcher from '@/utils/fetcher'
import VectorLayer from 'ol/layer/Vector'
import { WKT } from 'ol/format'
import { InfoReglementation } from '@/orrm/model/info-reglementation'
import { Entity } from '@/orrm/model/entity'
import TileWMS from 'ol/source/TileWMS'
import axios from 'axios'
import { Geometry, GeometryCollection, Point } from 'ol/geom'
import { Extent } from 'ol/extent'
import { Cluster } from 'ol/source'
import { Centroid, CentroidAttributes } from '@/orrm/model/centroid'
import AbstractClusterableNode from '@/orrm/model/AbstractClusterableNode'

@Component({
  components: { SearchInput, ScaleSelect, MapButton, ViewMode, ZoomButtons }
})
export default class MapContainer extends Vue {
  scale = 1000000
  ruler = false
  surfaceRuler = false
  draw?: Draw
  source: VectorSource<any> = new VectorSource({ wrapX: false })
  modalMessage: string | null = null
  onResolutionChangedDebounced = debounce(this.onResolutionChanged, 500)
  updateLayersDebounced = debounce(this.updateLayers, 500)
  updateFeaturesDebounced = debounce(this.updateFeatures, 1000)
  oldLayersDebounced?: LayerWrapper[]
  entitesCentroids: any[] = []
  actualitesCentroids: any[] = []
  infosCentroids: any[] = []

  get activeContext (): ContextWrapper | null {
    return context.activeContext
  }

  get searchModule () {
    return search
  }

  get map () {
    return MapSingleton.map
  }

  set map (value: Map) {
    MapSingleton.map = value
  }

  mounted () {
    this.map = new Map({
      target: this.$refs.mapOpenlayers as HTMLElement,
      controls: [],
      layers: [BaseMap.MONDE.layer],
      view: new View({
        center: [894357.16, 6334832.88],
        zoom: 1,
        projection: defaultProjection,
        resolutions: defaultScales.map(scale => getResolutionFromScale(defaultProjection.getUnits(), scale))
      })
    })
    this.map.getView().fit([779333.3667689643, 6213185.53465941, 1086451.3205318744, 6454977.293837391])
    this.scale = getScaleFromResolution(defaultProjection.getUnits(), this.map.getView().getResolution() || 0)

    if (map.viewType) {
      this.map.addLayer(map.viewType.layer)
    }

    this.map.getView().on('change:resolution', this.onResolutionChangedDebounced)
    this.map.on('moveend', this.updateFeaturesDebounced)
    this.map.on('singleclick', this.onSelectFeatures)
    this.onResolutionChangedDebounced()
    this.updateCentroids()
  }

  protected async onSelectFeatures (e: any): Promise<any | null> {
    if (!this.ruler && !this.surfaceRuler) {
      map.setLoading(true)
      map.showClickedFeatures({})
      const features: Feature<Geometry>[] = []
      const layersData: any[] = []
      const view = this.map.getView()
      this.map.forEachFeatureAtPixel(e.pixel, (feature) => {
        if (feature.get('features').length > 0) {
          for (const f of feature.get('features')) {
            features.push(f)
          }
        } else {
          features.push(feature as Feature<Geometry>)
        }
      })

      if (features.length > 0) {
        await this.updateItemsOnFeatures(features)
        map.setFeatures(features)
      }
      this.map.forEachLayerAtPixel(e.pixel, (layer) => {
        const source = layer.getSource()
        if (source instanceof TileWMS) {
          let url = source.getFeatureInfoUrl(e.coordinate, view.getResolution() || 0, defaultProjection, {
            FEATURE_COUNT: 1000
          })
          const urlParams = new URLSearchParams(url)
          const queryLayers = urlParams.get('layers')
          if (queryLayers) {
            url += `&QUERY_LAYERS=${queryLayers}`
          }
          if (url) {
            map.setLoading(true)
            axios.get(url).then((response) => {
              if (!/WMS server error/.test(response.data)) {
                const result = response.data.replace(/^GetFeatureInfo results:\n?/, '')
                layersData.push({ title: source.get('title'), result: result, rubric: layer.getProperties().getRubric() })
                map.showClickedFeatures({ features, layersData })
              }
            }).finally(() => {
              map.setLoading(false)
            })
          }
        }
      })
      map.showClickedFeatures({ features, layersData })
    }
  }

  refreshApp () {
    window.location.href = window.location.href // eslint-disable-line
  }

  onResolutionChanged () {
    const view = this.map.getView()
    const resolution = view.getResolution() || 0
    map.setResolution(resolution)
    this.scale = getScaleFromResolution(view.getProjection().getUnits(), resolution)
  }

  zoomIn () {
    const closestScale = defaultScales.reduce((a: number, b: number) => Math.abs(b - this.scale) < Math.abs(a - this.scale) ? b : a)
    const index = defaultScales.indexOf(closestScale)

    if (index >= 0 && index < defaultScales.length - 1) {
      this.scale = defaultScales[index + 1]
    }
  }

  zoomOut () {
    const closestScale = defaultScales.reduce((a: number, b: number) => Math.abs(b - this.scale) < Math.abs(a - this.scale) ? b : a)
    const index = defaultScales.indexOf(closestScale)

    if (index > 0) {
      this.scale = defaultScales[index - 1]
    }
  }

  clearInteraction () {
    if (this.draw) {
      this.map.removeInteraction(this.draw)
      this.draw = undefined
    }
  }

  async updateCentroids () {
    const entitesCentroidsPromise = Entity.centroidsResourceGetter()
    const actualitesCentroidsPromise = Actualite.centroidsResourceGetter()
    const infosCentroidsPromise = InfoReglementation.centroidsResourceGetter()

    entitesCentroidsPromise.then(centoids => { this.entitesCentroids = centoids.data })
    actualitesCentroidsPromise.then(centoids => { this.actualitesCentroids = centoids.data })
    infosCentroidsPromise.then(centoids => { this.infosCentroids = centoids.data })

    await Promise.all([
      entitesCentroidsPromise,
      actualitesCentroidsPromise,
      infosCentroidsPromise
    ])

    this.updateCentroidsLayers()
  }

  async updateItemsOnFeatures (features: Feature<Geometry>[]) {
    await Promise.all([
      this.attachItemFeaturesByType(Entity.layerId, features, this.fetchEntities,
        (item: Entity) => item.geometry ? new WKT().readFeature(item.geometry!.value) : null),
      this.attachItemFeaturesByType(InfoReglementation.layerId.toString(), features,
        this.fetchInfos,
        (item: InfoReglementation) => new WKT().readFeature(item.getCommune().geometry.value)),
      this.attachItemFeaturesByType(Actualite.layerId, features, this.fetchActu,
        (item: Actualite) => {
          const geometries = item.getCommunes().map(commune => new WKT().readGeometry(commune.geometry.value))
          return new Feature<any>(new GeometryCollection(geometries))
        })]
    )
  }

  updateCentroidsLayers () {
    this.updateCentroidsLayer(Entity.layerId, this.entitesCentroids)
    this.updateCentroidsLayer(Actualite.layerId, this.actualitesCentroids)
    this.updateCentroidsLayer(InfoReglementation.layerId, this.infosCentroids)
  }

  updateCentroidsLayer (layerId: string, centroids: Centroid[]) {
    if (!context.activeContext) return
    const layer = context.activeContext.getFakeLayer(layerId)
    const olLayer = layer?.olLayer as VectorLayer<any> | undefined

    if (!layer || !olLayer) return

    const source = olLayer.getSource()
    if (source) {
      source.clear()
    }

    layer.model.featuresList = centroids.map(centroid => {
      const feature = new Feature(new Point(centroid.attributes.centroid))
      feature.set('id', centroid.id)
      feature.set('centroid', centroid.attributes.centroid)
      feature.set('attributes', centroid.attributes)
      feature.set('belongsTo', layerId)
      return feature
    })

    olLayer.setSource(new Cluster({
      distance: 70,
      geometryFunction: feature => new Point(feature.get('centroid')),
      source: new VectorSource({ features: [...layer.model.featuresList] })
    }))

    olLayer.changed()
  }

  updateLayers (newLayers: LayerWrapper[]) {
    this.oldLayersDebounced = this.oldLayersDebounced || []
    const layers = [...newLayers, ...this.oldLayersDebounced]
    const managed: LayerWrapper[] = []
    let zindex = layers.length + 1
    for (const layer of layers) {
      zindex--
      if (~managed.indexOf(layer)) continue
      managed.push(layer)

      if (!layer.olLayer) continue
      if (~newLayers.indexOf(layer) && !~this.oldLayersDebounced.indexOf(layer)) { // S'il faut ajouter la couche
        this.map.addLayer(layer.olLayer)
      } else if (!~newLayers.indexOf(layer) && ~this.oldLayersDebounced.indexOf(layer)) { // S'il faut retirer la couche
        if (layer.model.layerType !== 'fake') { // On ignore les "fake layers"
          this.map.removeLayer(layer.olLayer)
        }
        continue
      }

      layer.olLayer.setOpacity(layer.opacity / 100)
      layer.olLayer.setVisible(layer.enabled)
      layer.olLayer.setZIndex(zindex)
    }
    this.oldLayersDebounced = clone(newLayers)
  }

  async updateFeatures () {
    return Promise.all([
      this.updateFeaturesByType <Entity>(
        Entity.layerId,
        this.fetchEntities,
        (item: Entity) => item.geometry ? new WKT().readFeature(item.geometry!.value) : null
      ),

      this.updateFeaturesByType <Actualite>(
        Actualite.layerId,
        this.fetchActu,
        (item: Actualite) => {
          const geometries = item.getCommunes().map(commune => new WKT().readGeometry(commune.geometry.value))
          return new Feature<any>(new GeometryCollection(geometries))
        }
      ),

      this.updateFeaturesByType <InfoReglementation>(
        InfoReglementation.layerId,
        this.fetchInfos,
        (item: InfoReglementation) => new WKT().readFeature(item.getCommune().geometry.value)
      )
    ])
  }

  async updateFeaturesByType<T extends AbstractClusterableNode> (layerId: string, fetcher: (bbox: Extent, ids?: string[]) => Promise<T[] | undefined>, itemToFeature: (item: T) => Feature<any> | null) {
    const bbox = this.map.getView().calculateExtent(this.map.getSize())
    if (!context.activeContext) return
    const layer = context.activeContext.getFakeLayer(layerId)
    if (!layer || !layer.enabled) return

    const source = layer.olLayer.getSource()
    if (source instanceof Cluster) {
      const features = source.getFeaturesInExtent(bbox)
      const idsToFetchMap: { [id: string]: Feature<Geometry> } = {}
      for (const feature of features) {
        if (feature.getGeometry()?.getType() === 'Point') {
          const children: Feature<Geometry>[] = feature.get('features')
          const explode = children.length <= 1 || getScaleFromResolution(defaultProjection.getUnits(), this.map.getView().getResolution() || 0) <= defaultScaleClusterExplosion
          if (explode) {
            for (const child of children) {
              if (!layer.model.cachedFeatures[child.get('id')]) {
                idsToFetchMap[child.get('id')] = child
              }
            }
          }
        }
      }

      const chunks = chunk(Object.keys(idsToFetchMap), 75)
      const chunkPromises: Promise<T[] | undefined>[] = []
      const fetched: T[] = []
      for (const chunk of chunks) {
        const prom = fetcher(bbox, chunk)
        chunkPromises.push(prom)
        prom.then(chunkFetched => {
          if (chunkFetched) {
            fetched.push(...chunkFetched)
          }
        })
      }
      await Promise.all(chunkPromises)

      // Les features sont toutes effacées puis réaffichées une fois que la mise à jour de toutes les géométries est terminées pour des raisons de perf.
      const vectorSource: VectorSource<any> = layer.olLayer?.getSource()?.getSource()
      if (vectorSource) {
        const allFeatures = [...vectorSource.getFeatures()]
        vectorSource.clear()
        for (const item of fetched) {
          const clusterCentroid = idsToFetchMap[item.uuid || '']
          if (clusterCentroid) {
            const newFeature = itemToFeature(item)
            if (newFeature) {
              clusterCentroid.setGeometry(newFeature.getGeometry())
              clusterCentroid.setProperties({ item: item })
              layer.model.cachedFeatures[item.uuid || ''] = newFeature
            }
          }
        }
        vectorSource.addFeatures(allFeatures)
      }
    }
  }

  async attachItemFeaturesByType<T extends AbstractClusterableNode> (layerId: string, features: Feature<Geometry>[], fetcher: (bbox: Extent, ids?: string[]) => Promise<T[] | undefined>, itemToFeature: (item: T) => Feature<any> | null) {
    const bbox = this.map.getView().calculateExtent(this.map.getSize())
    const featuresAttached: Feature<Geometry>[] = []
    if (!context.activeContext) return
    const layer = context.activeContext.getFakeLayer(layerId)
    if (!layer || !layer.enabled) return

    const idsToFetchMap: { [id: string]: Feature<Geometry> } = {}
    features = features.filter(feature => feature.get('belongsTo') === layerId)
    for (const feature of features) {
      if (feature.getGeometry()?.getType() === 'Point') {
        if (!layer.model.cachedFeatures[feature.get('id')]) {
          idsToFetchMap[feature.get('id')] = feature
        }
      }
    }
    if (features.length > 0) {
      const chunks = chunk(Object.keys(idsToFetchMap), 75)
      const chunkPromises: Promise<T[] | undefined>[] = []
      const fetched: T[] = []
      for (const chunk of chunks) {
        const prom = fetcher(bbox, chunk)
        chunkPromises.push(prom)
        prom.then(chunkFetched => {
          if (chunkFetched) {
            fetched.push(...chunkFetched)
          }
        })
      }
      await Promise.all(chunkPromises)

      for (const item of fetched) {
        const clusterCentroid = idsToFetchMap[item.uuid || '']
        if (clusterCentroid) {
          const newFeature = itemToFeature(item)
          if (newFeature) {
            clusterCentroid.setProperties({ item: item })
            featuresAttached.push(newFeature)
            layer.model.cachedFeatures[item.uuid || ''] = newFeature
          }
        }
      }
    }
  }

  filterFeaturesByType <T extends CentroidAttributes = CentroidAttributes> (layerId: string, filter: (centroids: Feature<any>[]) => Feature<any>[]) {
    if (!context.activeContext) return
    const layer = context.activeContext.getFakeLayer(layerId)
    const olLayer = layer?.olLayer as VectorLayer<any> | undefined
    if (!layer || !layer.enabled) return

    const filteredCentroids = filter(layer.model.featuresList)

    const clusterSource = olLayer?.getSource() as Cluster | undefined
    if (clusterSource) {
      const source = clusterSource.getSource()
      source.clear()
      source.addFeatures(filteredCentroids)
    }
  }

  fetchActu = async (bbox: Extent, ids?: string[]) => {
    if (!context.activeContext) return
    const layer = context.activeContext.getFakeLayer(Actualite.layerId)
    if (!layer || !layer.enabled) return

    const qb = Actualite.getQueryBuilder()
    qb.getQuery().addFilter(new FilterSpec('lon-min][condition][operator', '>='))
    qb.getQuery().addFilter(new FilterSpec('lon-min][condition][value', `${bbox[0]}`))
    qb.getQuery().addFilter(new FilterSpec('lon-min][condition][path', 'field_orrm_commune.field_orrm_geometrie.lon'))
    qb.getQuery().addFilter(new FilterSpec('lon-max][condition][operator', '<='))
    qb.getQuery().addFilter(new FilterSpec('lon-max][condition][value', `${bbox[2]}`))
    qb.getQuery().addFilter(new FilterSpec('lon-max][condition][path', 'field_orrm_commune.field_orrm_geometrie.lon'))
    qb.getQuery().addFilter(new FilterSpec('lat-min][condition][operator', '>='))
    qb.getQuery().addFilter(new FilterSpec('lat-min][condition][value', `${bbox[1]}`))
    qb.getQuery().addFilter(new FilterSpec('lat-min][condition][path', 'field_orrm_commune.field_orrm_geometrie.lat'))
    qb.getQuery().addFilter(new FilterSpec('lat-max][condition][operator', '<='))
    qb.getQuery().addFilter(new FilterSpec('lat-max][condition][value', `${bbox[3]}`))
    qb.getQuery().addFilter(new FilterSpec('lat-max][condition][path', 'field_orrm_commune.field_orrm_geometrie.lat'))
    if (ids) {
      qb.getQuery().addFilter(new FilterSpec('id][condition][operator', 'IN'))
      for (const id of ids) {
        qb.getQuery().addFilter(new FilterSpec('id][condition][value][', id))
      }
      qb.getQuery().addFilter(new FilterSpec('id][condition][path', 'id'))
    }

    const fetcher = new Fetcher(qb)
    await fetcher.fetchAll()

    return fetcher.datas
  }

  fetchInfos = async (bbox: Extent, ids?: string[]) => {
    if (!context.activeContext) return
    const layer = context.activeContext.getFakeLayer(InfoReglementation.layerId)
    if (!layer || !layer.enabled) return

    const qb = InfoReglementation.getQueryBuilder()
    qb.getQuery().addFilter(new FilterSpec('lon-min][operator', '>='))
    qb.getQuery().addFilter(new FilterSpec('lon-min][value', `${bbox[0]}`))
    qb.getQuery().addFilter(new FilterSpec('lon-min][path', 'commune.entity.field_orrm_geometrie.lon'))
    qb.getQuery().addFilter(new FilterSpec('lon-max][operator', '<='))
    qb.getQuery().addFilter(new FilterSpec('lon-max][value', `${bbox[2]}`))
    qb.getQuery().addFilter(new FilterSpec('lon-max][path', 'commune.entity.field_orrm_geometrie.lon'))
    qb.getQuery().addFilter(new FilterSpec('lat-min][operator', '>='))
    qb.getQuery().addFilter(new FilterSpec('lat-min][value', `${bbox[1]}`))
    qb.getQuery().addFilter(new FilterSpec('lat-min][path', 'commune.entity.field_orrm_geometrie.lat'))
    qb.getQuery().addFilter(new FilterSpec('lat-max][operator', '<='))
    qb.getQuery().addFilter(new FilterSpec('lat-max][value', `${bbox[3]}`))
    qb.getQuery().addFilter(new FilterSpec('lat-max][path', 'commune.entity.field_orrm_geometrie.lat'))
    if (ids) {
      qb.getQuery().addFilter(new FilterSpec('id][condition][operator', 'IN'))
      for (const id of ids) {
        qb.getQuery().addFilter(new FilterSpec('id][condition][value][', id))
      }
      qb.getQuery().addFilter(new FilterSpec('id][condition][path', 'uuid'))
    }

    const fetcher = new Fetcher(qb)
    await fetcher.fetchAll()

    return fetcher.datas
  }

  fetchEntities = async (bbox: Extent, ids?: string[]) => {
    if (!context.activeContext) return
    const layer = context.activeContext.getFakeLayer(Entity.layerId)
    if (!layer || !layer.enabled) return

    const qb = Entity.getQueryBuilder()
    qb.getQuery().addFilter(new FilterSpec('lon-min][condition][operator', '>='))
    qb.getQuery().addFilter(new FilterSpec('lon-min][condition][value', `${bbox[0]}`))
    qb.getQuery().addFilter(new FilterSpec('lon-min][condition][path', 'field_orrm_geometrie.lon'))
    qb.getQuery().addFilter(new FilterSpec('lon-max][condition][operator', '<='))
    qb.getQuery().addFilter(new FilterSpec('lon-max][condition][value', `${bbox[2]}`))
    qb.getQuery().addFilter(new FilterSpec('lon-max][condition][path', 'field_orrm_geometrie.lon'))
    qb.getQuery().addFilter(new FilterSpec('lat-min][condition][operator', '>='))
    qb.getQuery().addFilter(new FilterSpec('lat-min][condition][value', `${bbox[1]}`))
    qb.getQuery().addFilter(new FilterSpec('lat-min][condition][path', 'field_orrm_geometrie.lat'))
    qb.getQuery().addFilter(new FilterSpec('lat-max][condition][operator', '<='))
    qb.getQuery().addFilter(new FilterSpec('lat-max][condition][value', `${bbox[3]}`))
    qb.getQuery().addFilter(new FilterSpec('lat-max][condition][path', 'field_orrm_geometrie.lat'))

    if (ids) {
      qb.getQuery().addFilter(new FilterSpec('id][condition][operator', 'IN'))
      for (const id of ids) {
        qb.getQuery().addFilter(new FilterSpec('id][condition][value][', id))
      }
      qb.getQuery().addFilter(new FilterSpec('id][condition][path', 'id'))
    }

    const fetcher = new Fetcher(qb)
    await fetcher.fetchAll()

    return fetcher.datas
  }

  updateClusterSource (layerId: string) {
    if (!context.activeContext) return
    const layer = context.activeContext.getFakeLayer(layerId)
    if (!layer || !layer.enabled) return
    const source = layer.olLayer.getSource()
    if (source instanceof Cluster) {
      if (this.scale <= defaultScaleClusterExplosion && source.getDistance() !== 0) {
        source.setDistance(0)
        source.changed()
      } else if (this.scale > defaultScaleClusterExplosion && source.getDistance() === 0) {
        source.setDistance(20)
        source.changed()
      }
    }
  }

  @Watch('activeContext')
  async onActiveContextChanged () {
    await this.updateFeatures()
  }

  get activeContextLayersWatchValues () {
    return (this.activeContext?.layers || []).map(layer => {
      return {
        uuid: layer.model.uuid,
        opacity: layer.opacity,
        enabled: layer.enabled
      }
    })
  }

  @Watch('activeContextLayersWatchValues', { immediate: true, deep: true })
  onActiveLayersChanged () {
    this.updateLayersDebounced(this.activeContext?.layers || [])
  }

  @Watch('scale')
  onScaleChanged (newVal: number) {
    const view = this.map.getView()
    const resolution = getResolutionFromScale(view.getProjection().getUnits(), newVal)
    const zoom = view.getZoomForResolution(resolution) || 1
    view.setZoom(zoom)

    this.updateClusterSource(Entity.layerId)
    this.updateClusterSource(Actualite.layerId)
    this.updateClusterSource(InfoReglementation.layerId)
  }

  @Watch('ruler')
  onRulerToggled (enabled: boolean) {
    this.clearInteraction()
    if (enabled) {
      this.draw = new Draw({ source: this.source, type: GeometryType.LINE_STRING })
      this.draw.on('drawend', (e: DrawEvent) => {
        const length = (e.feature.getGeometry() as LineString).getLength()
        let formattedLength: string
        if (length > 100) {
          formattedLength = Math.round((length / 1000) * 100) / 100 + ' ' + 'km'
        } else {
          formattedLength = Math.round(length * 100) / 100 + ' ' + 'm'
        }

        this.modalMessage = `Distance mesurée: <b>${formattedLength}</b>`
        this.$bvModal.show('message-modal')
        this.ruler = false
      })
      this.map.addInteraction(this.draw)
    }
  }

  @Watch('surfaceRuler')
  onSurfaceRulerToggled (enabled: boolean) {
    this.clearInteraction()
    if (enabled) {
      this.draw = new Draw({ source: this.source, type: GeometryType.POLYGON })
      this.draw.on('drawend', (e: DrawEvent) => {
        const area = (e.feature.getGeometry() as Polygon).getArea()
        let formattedArea: string
        if (area > 10000) {
          formattedArea = Math.round((area / 1000000) * 100) / 100 + ' ' + 'km<sup>2</sup>'
        } else {
          formattedArea = Math.round(area * 100) / 100 + ' ' + 'm<sup>2</sup>'
        }
        this.modalMessage = `Surface mesurée: <b>${formattedArea}</b>`
        this.$bvModal.show('message-modal')
        this.surfaceRuler = false
      })
      this.map.addInteraction(this.draw)
    }
  }

  @Watch('searchModule.actuCriterias', { deep: true })
  onActuCriteriasChanged () {
    this.filterFeaturesByType(Actualite.layerId, this.searchModule.actuFilter)
  }

  @Watch('searchModule.entityCriterias', { deep: true })
  onEntityCriteriasChanged () {
    this.filterFeaturesByType(Entity.layerId, this.searchModule.entityFilter)
  }

  @Watch('searchModule.infoCriterias', { deep: true })
  onInfoCriteriasChanged () {
    this.filterFeaturesByType(InfoReglementation.layerId, this.searchModule.infosFilter)
  }
}
